import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CssBaseline, GlobalStyles, Grid, Typography, Paper } from '@mui/material';
import { Navbar } from './components/Navbar';
import { FixedCTA } from './components/FixedCTA';
import { Footer } from './components/Footer';
import { HeroSection } from './components/HeroSection';
import { VimeoSection } from './components/VimeoSection';
import { PricingSection } from './components/PricingSection';
import { TrustBar } from './components/TrustBar';
import { TestimonialsSection } from './components/TestimonialsSection';
import { FAQSection } from './components/FAQSection';
import { useTranslation, Language } from '../i18n';
import { GradientBackground } from '../components/ui/GradientBackground';
import { seoTranslations } from './translations/seo';
import { Player } from '@lottiefiles/react-lottie-player';
import lottieAnimation1 from '../assets/lottie/lottie1.json';
import lottieAnimation2 from '../assets/lottie/lottie2.json';
import lottieAnimation3 from '../assets/lottie/lottie3.json';
import lottieAnimation4 from '../assets/lottie/lottie4.json';
import lottieAnimation5 from '../assets/lottie/lottie5.json';
import lottieAnimation6 from '../assets/lottie/lottie6.json';
import lottieAnimation7 from '../assets/lottie/lottie7.json';
import lottieAnimation8 from '../assets/lottie/lottie8.json';
import lottieAnimation9 from '../assets/lottie/lottie9.json';
import { useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Contexte de langue global
export const LangContext = createContext<{
  lang: Language;
  setLang: (lang: Language) => void;
}>({
  lang: 'fr',
  setLang: () => {}
});

// Composant WhySection
const WhySectionComponent: React.FC = () => {
  const { lang } = useContext(LangContext);
  const { t } = useTranslation(lang);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const gradientShift = keyframes`
    0% { background-position: 0% 50%; filter: hue-rotate(0deg) saturate(1.05) contrast(1.05); }
    50% { background-position: 100% 50%; filter: hue-rotate(18deg) saturate(1.05) contrast(1.05); }
    100% { background-position: 0% 50%; filter: hue-rotate(0deg) saturate(1.05) contrast(1.05); }
  `;
  
  const features = [
    'simple',
    'sync',
    'rapide',
    'puissant',
    'accessible',
    'secure',
    'intuitif',
    'evolutif',
    'collab'
  ];

  return (
    <Box 
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 8 },
        px: { xs: 2, sm: 4, md: 6 },
        backgroundColor: 'black'
      }}
    >
      {/* Vidéo de fond spécifique section 2 - désactivée sur mobile */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.22,
              backgroundImage:
                'radial-gradient(900px circle at 15% 20%, rgba(88,101,242,0.55), transparent 58%),'
                + 'radial-gradient(700px circle at 85% 25%, rgba(0,229,255,0.28), transparent 62%),'
                + 'radial-gradient(850px circle at 45% 80%, rgba(186,104,200,0.26), transparent 62%),'
                + 'linear-gradient(120deg, rgba(14,18,28,1), rgba(0,0,0,1))',
              backgroundSize: '200% 200%',
              animation: `${gradientShift} 40s ease-in-out infinite`,
              willChange: 'background-position, filter',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.16)',
            }}
          />
        </Box>
      )}

      {/* Contenu de la section */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 6,
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            color: 'primary.main'
          }}
        >
          {t('landing.sections.why.title')}
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {features.map((featureId) => (
            <Grid item xs={12} sm={6} md={4} key={featureId}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  }
                }}
              >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {featureId === 'simple' ? (
                  <Player
                    src={lottieAnimation1}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'sync' ? (
                  <Player
                    src={lottieAnimation2}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'rapide' ? (
                  <Player
                    src={lottieAnimation3}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'puissant' ? (
                  <Player
                    src={lottieAnimation4}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'accessible' ? (
                  <Player
                    src={lottieAnimation5}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'secure' ? (
                  <Player
                    src={lottieAnimation6}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'intuitif' ? (
                  <Player
                    src={lottieAnimation7}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'evolutif' ? (
                  <Player
                    src={lottieAnimation8}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : featureId === 'collab' ? (
                  <Player
                    src={lottieAnimation9}
                    loop
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  /* Placeholder pour les autres icônes */
                  <Box sx={{ width: 40, height: 40, bgcolor: 'primary.main', borderRadius: '50%' }} />
                )}
              </Box>
              
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  {t(`landing.sections.why.features.${featureId}.title`)}
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    flexGrow: 1
                  }}
                >
                  {t(`landing.sections.why.features.${featureId}.description`)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang, setLang } = useContext(LangContext);
  const { t } = useTranslation(lang);
  const seoData = seoTranslations[lang];

  const handleContactClick = () => {
    window.open('mailto:contact@tacticafoot.com');
  };

  // Détecter si nous sommes sur mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Thème clair pour mobile
  const parentTheme = useTheme();
  const mobileTheme = useMemo(() => createTheme(parentTheme, { palette: { mode: 'light' } }), [parentTheme]);
  const effectiveTheme = isMobile ? mobileTheme : parentTheme;

  // Effet pour masquer le GradientBackground sur mobile
  useEffect(() => {
    if (isMobile) {
      console.log('LandingPage - Détection mobile, masquage du gradient');
      // Laisser la vidéo de fond visible : pas de couleur forcée sur body / html
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.backgroundColor = 'transparent';
    }
  }, [isMobile]);
  
  return (
    <ThemeProvider theme={effectiveTheme}>
      <LangContext.Provider value={{ lang, setLang }}>
        <Box sx={{ 
          minHeight: '100vh', 
          position: 'relative',
          background: 'transparent'
        }}>
          <CssBaseline />
          <GlobalStyles
            styles={{
              '*': {
                boxSizing: 'border-box',
                margin: 0,
                padding: 0,
              },
              'html, body': {
                scrollBehavior: 'smooth',
                background: 'transparent',
              },
              '.section-transition': {
                transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
              }
            }}
          />
          <GradientBackground />
          
          <Box 
            sx={{
              position: 'relative',
              zIndex: 1,
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Navbar onContactClick={handleContactClick} />
            <Box 
              className="landing-page"
              sx={{ 
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                overflowY: 'auto',
                '& > *:not(:last-child)': {
                  marginBottom: {
                    xs: '2px',  
                    sm: '32px', 
                    md: '40px'  
                  }
                }
              }}
            >
              <HeroSection
                title={t('landing.hero.title')}
                secondaryButton={{
                  text: t('landing.hero.login'),
                  onClick: () => window.location.href = 'https://app.tacticafoot.com/connexion'
                }}
              />
              <TrustBar />
              <VimeoSection
                title="landing.sections.video.title"
                videoUrl="https://embed.eu.videocenter.ovh.net/vod/vod_1Cabf1YNE4xgUybm5FPRkC"
              />
              <WhySectionComponent />
              <TestimonialsSection preview />
              <FAQSection preview />
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                width: '100%',
                position: 'relative',
                zIndex: 10 
              }}>
                <PricingSection title={t('landing.sections.pricing.title')} />
              </Box>
            </Box>
            <Box 
              sx={{ 
                width: '100%',
                position: 'relative',
                zIndex: 20,
                mt: 'auto'
              }}
            >
              <Footer />
            </Box>
            <FixedCTA />
          </Box>
        </Box>
      </LangContext.Provider>
    </ThemeProvider>
  );
};
